import {
  Badge, Icon, useColorModeValue, Circle, Avatar, Box,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { FiX } from "react-icons/fi";
import useTargetName from "hooks/useTargetName";
import { MeetingAttendee, MeetingResponseStatus } from "features/Resources/Meeting/models";
import { TemplateMeetingAttendee } from "features/Resources/MeetingTemplate/models";
import CaminoTentativeQuestionMark from "components/Icon/CaminoTentativeQuestionMark";
import { UserRole } from "features/User/models";
import { AiOutlineEllipsis } from "react-icons/ai";
import CaminoCrownIcon from "components/Icon/CaminoCrownIcon";
import { roleStyles } from "../UserTag/helpers";
import BaseResourceTargetTag, { BaseResourceTargetTagProps } from "./BaseResourceTargetTag";

function LeadBadge() {
  return (
    <Badge bg="cdsGrey.800" color="white" fontSize="8px" lineHeight="8px" borderRadius="2px">
      Lead
    </Badge>
  );
}

function LeadAvatarIndicator() {
  return (
    <Circle bgColor="cdsGrey.800" size="3">
      <CaminoCrownIcon p="2px" />
    </Circle>
  );
}

function attendingIconConfig(responseStatus?: MeetingResponseStatus) {
  switch (responseStatus) {
    case "pending":
      return { icon: AiOutlineEllipsis, bgColor: "disabledGray.DEFAULT" };
    case "accepted":
      return { icon: CheckIcon, bgColor: "green.400" };
    case "declined":
      return { icon: FiX, bgColor: "red.400" };
    case "tentative":
      return { icon: CaminoTentativeQuestionMark, bgColor: "disabledGray.DEFAULT" };
    default:
      return null;
  }
}

function ResponseStatusIndicator({ responseStatus }: { responseStatus?: MeetingResponseStatus }) {
  const iconStyle = attendingIconConfig(responseStatus);
  if (!iconStyle) {
    return null;
  }
  return (
    <Circle
      bgColor={iconStyle.bgColor}
      size="3"
      zIndex={1}
    >
      <Icon
        as={iconStyle.icon}
        strokeWidth="3"
        color="white"
        boxSize="2"
      />
    </Circle>
  );
}

interface AttendeeNameTagProps extends BaseResourceTargetTagProps {
  isLeader?: boolean;
  responseStatus?: MeetingResponseStatus;
}

export function AttendeeNameTag({
  name,
  responseStatus = undefined,
  isLeader = false,
  roleName,
  size = "sm",
  remove = undefined,
  disabled = false,
}: AttendeeNameTagProps) {
  return (
    <Box position="relative">
      <BaseResourceTargetTag name={name} roleName={roleName} size={size} remove={remove} disabled={disabled} />

      {isLeader && (
        <Box
          position="absolute"
          top="-16px"
          left="-4px"
        >
          <LeadBadge />
        </Box>
      )}

      <Box
        position="absolute"
        top="-6px"
        right="-6px"
      >
        <ResponseStatusIndicator responseStatus={responseStatus} />
      </Box>
    </Box>
  );
}

type AttendeeAvatarProps = {
  name?: string;
  src?: string;
  responseStatus?: MeetingResponseStatus;
  isLeader?: boolean;
  roleName?: UserRole;
};

export function AttendeeAvatar({
  name,
  src,
  responseStatus = undefined,
  isLeader = false,
  roleName,
}: AttendeeAvatarProps) {
  const colorStyles = roleStyles(roleName);
  const bgColor = useColorModeValue(
    colorStyles.light,
    colorStyles.dark,
  );
  const config = attendingIconConfig(responseStatus);

  return (
    <Box position="relative">
      <Avatar
        size="sm"
        name={name}
        src={src}
        bgColor={bgColor}
        color="gray.500"
        showBorder
        borderColor={config?.bgColor}
        borderWidth="1px"
      />

      {isLeader && (
        <Box
          position="absolute"
          top="-6px"
          left="50%"
          transform="translateX(-50%)"
        >
          <LeadAvatarIndicator />
        </Box>
      )}

      {responseStatus !== "pending" && (
        <Box
          position="absolute"
          bottom="-6px"
          left="50%"
          transform="translateX(-50%)"
        >
          <ResponseStatusIndicator responseStatus={responseStatus} />
        </Box>
      )}
    </Box>
  );
}

export function MeetingAttendeeAvatar({ attendee }: { attendee: MeetingAttendee }) {
  let { name, role: derivedRole } = useTargetName({
    resourceId: attendee?.userId,
    resourceType: "User",
    options: { enabled: !attendee.emailAddress },
    loadingLabel: null,
  });
  if (attendee?.emailAddress) {
    name = attendee.emailAddress;
    derivedRole = undefined;
  }

  return (
    <AttendeeAvatar
      name={name || undefined}
      src={attendee?.user?.avatarUrl}
      responseStatus={attendee.responseStatus}
      isLeader={attendee.isLead}
      roleName={attendee.role || derivedRole}
    />
  );
}

interface MeetingAttendeeTagProps {
  attendee: MeetingAttendee;
  isLeader: boolean;
  responseStatus: MeetingResponseStatus;
  role: UserRole | undefined;
  remove?: () => void;
  disabled?: boolean;
}

export default function MeetingAttendeeTag({
  attendee, role, responseStatus, isLeader = false, remove = undefined, disabled = false,
}: MeetingAttendeeTagProps) {
  let { name, role: derivedRole } = useTargetName({ resourceId: attendee?.userId, resourceType: "User", options: { enabled: !attendee.emailAddress } });
  if (attendee?.emailAddress) {
    name = attendee.emailAddress;
    derivedRole = undefined;
  }

  return (
    <AttendeeNameTag
      name={name || undefined}
      disabled={disabled}
      roleName={role || derivedRole}
      remove={remove}
      isLeader={isLeader}
      responseStatus={responseStatus}
    />
  );
}

interface TemplateMeetingAttendeeTagProps {
  templateAttendee: TemplateMeetingAttendee;
  isLeader: boolean;
  role: UserRole | undefined;
  remove?: () => void;
  disabled?: boolean;
}

export function TemplateMeetingAttendeeTag({
  templateAttendee, role, isLeader = false, remove = undefined, disabled = false,
}: TemplateMeetingAttendeeTagProps) {
  let { name, role: derivedRole } = useTargetName({
    resourceId: templateAttendee?.attendeeId,
    resourceType: templateAttendee?.attendeeType,
    options: { enabled: !templateAttendee.emailAddress && !!templateAttendee?.attendee?.id },
  });
  if (templateAttendee.emailAddress) {
    name = templateAttendee.emailAddress;
    derivedRole = undefined;
  }

  return (
    <AttendeeNameTag
      name={name || undefined}
      disabled={disabled}
      roleName={role || derivedRole}
      remove={remove}
      isLeader={isLeader}
    />
  );
}
